import { SimpleGame } from "@/app";
import getDecorators from "inversify-inject-decorators";
import container from "../../../../ioc/inversify.config";
import TYPES from "../../../../ioc/types";
import { AuthenticatedAssetLoader } from "../../../../services/authenticated-asset-loader";
import { GameStateDTO } from "../../../../shared/dtos";
import { Endpoints, SceneId } from "../../../../shared/enum";
import { LoadGameMenu } from "../../../menu/title-screen/load-game-menu";
import { drawBackground } from "../../../utils/scene-background";

const { lazyInject } = getDecorators(container);

export class LoadGameScene extends Phaser.Scene {
    private menu: LoadGameMenu;
    private leftArrow: Phaser.GameObjects.Triangle;
    private rightArrow: Phaser.GameObjects.Triangle;
    private pageIndicator: Phaser.GameObjects.Text;

    @lazyInject(TYPES.AuthenticatedAssetLoader)
    private assetLoader: AuthenticatedAssetLoader;

    constructor() {
        super({ key: SceneId.LOAD_GAME_SCREEN });
    }

    init() {
        this.sys.events.on("wake", () => this.scene.bringToTop());
        this.scene.bringToTop();
    }

    public create(sceneId: string) {
        drawBackground(this);

        // Load authenticated game saves data
        this.loadAuthenticatedAssets()
            .then(() => {
                const saves = this.cache.json.get("game_saves") as GameStateDTO[];
                this.menu = new LoadGameMenu(0, 0, 0, 0, saves, this);
                // Add the menu container to the scene so it's actually rendered
                this.add.existing(this.menu);

                // Create navigation arrows and page indicator
                this.createNavigationUI();

                // Set up callback to update UI when page changes
                this.menu.setPageChangeCallback((page, totalPages) => {
                    this.updateNavigationUI(page, totalPages);
                });
            })
            .catch((error) => {
                console.error("Failed to load game saves:", error);
            });
    }

    private createNavigationUI() {
        // Left arrow
        this.leftArrow = this.add.triangle(
            50,
            this.cameras.main.height / 2,
            0,
            0, // Point (left)
            20,
            -15, // Top
            20,
            15, // Bottom
            0xffffff
        );
        this.leftArrow.setInteractive({ useHandCursor: true });
        this.leftArrow.on("pointerdown", () => {
            if (this.menu.canGoToPreviousPage()) {
                this.menu.previousPage();
            }
        });
        this.leftArrow.on("pointerover", () => {
            if (this.menu.canGoToPreviousPage()) {
                this.leftArrow.setScale(1.2);
            }
        });
        this.leftArrow.on("pointerout", () => {
            this.leftArrow.setScale(1.0);
        });

        // Right arrow
        this.rightArrow = this.add.triangle(
            this.cameras.main.width - 50,
            this.cameras.main.height / 2,
            20,
            0, // Point (right)
            0,
            -15, // Top
            0,
            15, // Bottom
            0xffffff
        );
        this.rightArrow.setInteractive({ useHandCursor: true });
        this.rightArrow.on("pointerdown", () => {
            if (this.menu.canGoToNextPage()) {
                this.menu.nextPage();
            }
        });
        this.rightArrow.on("pointerover", () => {
            if (this.menu.canGoToNextPage()) {
                this.rightArrow.setScale(1.2);
            }
        });
        this.rightArrow.on("pointerout", () => {
            this.rightArrow.setScale(1.0);
        });

        // Page indicator
        this.pageIndicator = this.add.text(
            this.cameras.main.width / 2,
            this.cameras.main.height - 50,
            "",
            {
                fontSize: "16px",
                color: "#ffffff",
                fontFamily: "monospace",
            }
        );
        this.pageIndicator.setOrigin(0.5);
    }

    private updateNavigationUI(page: number, totalPages: number) {
        // Safety check - UI might not be created yet
        if (!this.leftArrow || !this.rightArrow || !this.pageIndicator) {
            return;
        }

        // Update arrow visibility
        this.leftArrow.setVisible(page > 0);
        this.rightArrow.setVisible(page < totalPages - 1);

        // Update page indicator
        if (totalPages > 1) {
            this.pageIndicator.setText(`Page ${page + 1} / ${totalPages}`);
            this.pageIndicator.setVisible(true);
        } else {
            this.pageIndicator.setVisible(false);
        }

        // Update arrow opacity based on availability
        this.leftArrow.setAlpha(page > 0 ? 1.0 : 0.3);
        this.rightArrow.setAlpha(page < totalPages - 1 ? 1.0 : 0.3);
    }

    private async loadAuthenticatedAssets(): Promise<void> {
        const url = `${Endpoints.BASE_URL}/${Endpoints.GAME_STATE}/load-games/?projectId=${SimpleGame.PROJECT_ID}`;
        console.log("Url ", url);
        await this.assetLoader.loadJson(this, "game_saves", url);
    }
}
